/**
 * AGDataGrid - Reusable AG Grid wrapper component
 * 
 * Provides:
 * - Integrated search toolbar with optional edit mode toggle
 * - Server-side pagination controls
 * - Loading overlays
 * - Theme-aware styling (via ag-theme-quartz CSS)
 * - Configurable column definitions with conditional editing
 */

import { useMemo, useRef, useCallback, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ModuleRegistry,
  ColDef,
  GridReadyEvent,
  GridApi,
  CellValueChangedEvent,
  SelectionChangedEvent,
  RowClassParams
} from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import { Loader2, Search, X, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Pencil, PencilOff, Columns3 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

// Register AG Grid Enterprise modules + license
const AG_LICENSE_KEY = import.meta.env.VITE_AG_GRID_LICENSE_KEY;
if (AG_LICENSE_KEY) {
  LicenseManager.setLicenseKey(AG_LICENSE_KEY);
}
ModuleRegistry.registerModules([AllEnterpriseModule]);

// localStorage helpers for column state persistence (mirrors usePagination.ts pattern)
const COLSTATE_PREFIX = 'colstate_';
const getColState = (key: string): string | null => {
  try { return localStorage.getItem(`${COLSTATE_PREFIX}${key}`); } catch { return null; }
};
const setColState = (key: string, value: string): void => {
  try { localStorage.setItem(`${COLSTATE_PREFIX}${key}`, value); } catch { /* silently fail */ }
};

export interface AGDataGridProps<TData> {
  /** Row data to display */
  rowData: TData[];
  /** Column definitions - should use useMemo in parent */
  columnDefs: ColDef<TData>[];
  /** Default column settings */
  defaultColDef?: ColDef<TData>;
  /** Total count for pagination */
  totalCount: number;
  /** Current page index (0-based) */
  pageIndex: number;
  /** Page size */
  pageSize: number;
  /** Available page size options */
  pageSizeOptions?: number[];
  /** Current search value */
  searchValue?: string;
  /** Search placeholder text */
  searchPlaceholder?: string;
  /** Whether data is loading (initial load) */
  isLoading?: boolean;
  /** Whether data is fetching (background refresh) */
  isFetching?: boolean;
  /** Whether edit mode is enabled */
  isEditMode?: boolean;
  /** Whether to show the edit mode toggle button */
  showEditToggle?: boolean;
  /** Grid height (CSS value or 'auto') */
  height?: string;
  /** Minimum height when using dynamic height */
  minHeight?: string;
  
  // Callbacks
  /** Called when search value changes */
  onSearchChange?: (value: string) => void;
  /** Called on search input keydown */
  onSearchKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Called when search input gains focus */
  onSearchFocus?: () => void;
  /** Called when search input loses focus */
  onSearchBlur?: () => void;
  /** Context object passed to AG Grid cell renderers */
  gridContext?: any;
  /** Called when page index changes */
  onPageChange?: (pageIndex: number) => void;
  /** Called when page size changes */
  onPageSizeChange?: (pageSize: number) => void;
  /** Called when edit mode is toggled */
  onEditModeChange?: (isEditMode: boolean) => void;
  /** Called when a cell value changes (for inline editing) */
  onCellValueChanged?: (event: CellValueChangedEvent<TData>) => void;
  /** Called when grid is ready */
  onGridReady?: (event: GridReadyEvent<TData>) => void;
  /** Called when a row is clicked */
  onRowClicked?: (data: TData) => void;
  /** Called when a row is double-clicked */
  onRowDoubleClicked?: (data: TData) => void;
  /** Override row selection mode (default: "single"). Accepts AG Grid v35 object config. */
  rowSelection?: any;
  /** Called when row selection changes */
  onSelectionChanged?: (event: SelectionChangedEvent<TData>) => void;

  /** Optional key for persisting column visibility to localStorage.
   *  When set, hidden/shown columns are saved and restored across sessions. */
  columnStateKey?: string;

  // Optional toolbar customization
  /** Additional toolbar content (rendered after search) */
  toolbarActions?: React.ReactNode;
  /** Custom row class callback for conditional row styling */
  getRowClass?: (params: RowClassParams<TData>) => string | string[] | undefined;
  /** How many group levels to expand by default (-1 = all) */
  groupDefaultExpanded?: number;
  /** Override the auto-generated group column definition */
  autoGroupColumnDef?: ColDef<TData>;
}

export function AGDataGrid<TData>({
  rowData,
  columnDefs,
  defaultColDef: customDefaultColDef,
  totalCount,
  pageIndex,
  pageSize,
  pageSizeOptions = [10, 25, 50, 100],
  searchValue = '',
  searchPlaceholder = 'Search...',
  isLoading = false,
  isFetching = false,
  isEditMode = false,
  showEditToggle = false,
  height = 'calc(100vh - 480px)',
  minHeight = '300px',
  columnStateKey,
  onSearchChange,
  onSearchKeyDown,
  onSearchFocus,
  onSearchBlur,
  gridContext,
  onPageChange,
  onPageSizeChange,
  onEditModeChange,
  onCellValueChanged,
  onGridReady,
  onRowClicked,
  onRowDoubleClicked,
  rowSelection: rowSelectionProp,
  onSelectionChanged,
  toolbarActions,
  getRowClass,
  groupDefaultExpanded,
  autoGroupColumnDef,
}: AGDataGridProps<TData>) {
  const gridApiRef = useRef<GridApi<TData> | null>(null);
  const [columnsPanelOpen, setColumnsPanelOpen] = useState(false);

  // Sidebar configuration for Columns Tool Panel
  const sideBar = useMemo(() => ({
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressRowGroups: true,
          suppressValues: true,
          suppressPivots: true,
          suppressPivotMode: true,
        },
      },
    ],
    position: 'right' as const,
    defaultToolPanel: '',
  }), []);

  // Toggle columns tool panel
  const toggleColumnsPanel = useCallback(() => {
    if (!gridApiRef.current) return;
    if (gridApiRef.current.getOpenedToolPanel() === 'columns') {
      gridApiRef.current.closeToolPanel();
      setColumnsPanelOpen(false);
    } else {
      gridApiRef.current.openToolPanel('columns');
      setColumnsPanelOpen(true);
    }
  }, []);

  // Debounced save of column visibility to localStorage
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleColumnVisible = useCallback(() => {
    if (!columnStateKey || !gridApiRef.current) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      const fullState = gridApiRef.current!.getColumnState();
      const hiddenCols = fullState
        .filter((col) => col.hide)
        .map((col) => ({ colId: col.colId, hide: true }));
      setColState(columnStateKey, JSON.stringify(hiddenCols));
    }, 300);
  }, [columnStateKey]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  // Default column settings
  const defaultColDef = useMemo<ColDef<TData>>(() => ({
    resizable: true,
    sortable: true,
    suppressHeaderMenuButton: true,
    ...customDefaultColDef,
  }), [customDefaultColDef]);

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const startRow = totalCount === 0 ? 0 : pageIndex * pageSize + 1;
  const endRow = Math.min((pageIndex + 1) * pageSize, totalCount);

  // Grid ready handler — restore saved column visibility
  const handleGridReady = useCallback((params: GridReadyEvent<TData>) => {
    gridApiRef.current = params.api;

    if (columnStateKey) {
      const raw = getColState(columnStateKey);
      if (raw) {
        try {
          const hiddenCols: { colId: string; hide: boolean }[] = JSON.parse(raw);
          if (Array.isArray(hiddenCols) && hiddenCols.length > 0) {
            params.api.applyColumnState({
              state: hiddenCols,
              defaultState: { hide: false },
            });
          }
        } catch {
          // Corrupted data — ignore silently
        }
      }
    }

    onGridReady?.(params);
  }, [onGridReady, columnStateKey]);

  // Refresh cells when grid context changes (AG Grid doesn't do this automatically)
  useEffect(() => {
    if (gridApiRef.current && gridContext !== undefined) {
      gridApiRef.current.refreshCells({ force: true });
    }
  }, [gridContext]);

  // Row click handlers — skip when the user clicked a selection checkbox
  const handleRowClicked = useCallback((event: { data?: TData; event?: Event }) => {
    const target = event.event?.target as HTMLElement | undefined;
    if (target?.closest('.ag-selection-checkbox')) return;

    if (event.data && onRowClicked) {
      onRowClicked(event.data);
    }
  }, [onRowClicked]);

  const handleRowDoubleClicked = useCallback((event: { data?: TData }) => {
    if (event.data && onRowDoubleClicked) {
      onRowDoubleClicked(event.data);
    }
  }, [onRowDoubleClicked]);

  // Pagination handlers
  const goToFirstPage = () => onPageChange?.(0);
  const goToPreviousPage = () => onPageChange?.(Math.max(0, pageIndex - 1));
  const goToNextPage = () => onPageChange?.(Math.min(totalPages - 1, pageIndex + 1));
  const goToLastPage = () => onPageChange?.(totalPages - 1);

  const handlePageSizeChange = (value: string) => {
    onPageSizeChange?.(parseInt(value, 10));
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 bg-card border border-border rounded-lg">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="p-4 border-b border-border flex items-center justify-between gap-4">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => onSearchChange?.(e.target.value)}
            onKeyDown={onSearchKeyDown}
            onFocus={onSearchFocus}
            onBlur={onSearchBlur}
            className={searchValue ? "pl-9 pr-8" : "pl-9"}
          />
          {searchValue && (
            <button
              type="button"
              onClick={() => onSearchChange?.('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              title="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {toolbarActions}

          <Button
            variant={columnsPanelOpen ? "default" : "outline"}
            size="icon"
            onClick={toggleColumnsPanel}
            title="Toggle columns"
          >
            <Columns3 className="w-4 h-4" />
          </Button>

          {showEditToggle && (
            <Button
              variant={isEditMode ? "default" : "outline"}
              size="icon"
              onClick={() => onEditModeChange?.(!isEditMode)}
              title={isEditMode ? "Switch to view mode" : "Enable inline editing"}
            >
              {isEditMode ? <PencilOff className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
            </Button>
          )}
        </div>
      </div>

      {/* Grid */}
      <div
        className="ag-theme-quartz relative [&_.ag-side-buttons]:hidden"
        style={{ height, minHeight, width: '100%' }}
      >
        {isFetching && !isLoading && (
          <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        )}
        <AgGridReact<TData>
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          sideBar={sideBar}
          rowHeight={50}
          animateRows={true}
          rowSelection={rowSelectionProp ?? { mode: 'singleRow' }}
          suppressCellFocus={!isEditMode}
          domLayout="normal"
          context={gridContext}
          onGridReady={handleGridReady}
          onCellValueChanged={onCellValueChanged}
          onRowClicked={handleRowClicked}
          onRowDoubleClicked={handleRowDoubleClicked}
          onSelectionChanged={onSelectionChanged}
          onColumnVisible={handleColumnVisible}
          getRowClass={getRowClass}
          stopEditingWhenCellsLoseFocus={true}
          singleClickEdit={isEditMode}
          groupDefaultExpanded={groupDefaultExpanded}
          autoGroupColumnDef={autoGroupColumnDef}
        />
      </div>

      {/* Pagination controls */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/30">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Rows per page:</span>
          <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {totalCount === 0 
              ? 'No items' 
              : `${startRow}-${endRow} of ${totalCount}`}
          </span>
          
          <div className="flex items-center gap-1 ml-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={goToFirstPage}
              disabled={pageIndex === 0}
              title="First page"
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={goToPreviousPage}
              disabled={pageIndex === 0}
              title="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={goToNextPage}
              disabled={pageIndex >= totalPages - 1}
              title="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={goToLastPage}
              disabled={pageIndex >= totalPages - 1}
              title="Last page"
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
